import React, { useState, useRef, Suspense, useEffect } from 'react';
import Spinner from 'design-system/Spinner';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from 'design-system';
import { makeStyles } from 'react-scale/components/design-system/styles';

import TaskComponent from 'react-scale/components/Task/TaskComponent';
import { ShortcutsProvider } from 'react-scale/hooks/useShortcut';

import { sampleTask } from '@scale/shared/selfServe/validationHelpers';
import { Project } from 'dashboard/types';
import API from '@scale/frontend-lib/dashboard-api';
import { createParams } from 'react-scale/dashboard.scale.com/components/SelfServe/helpers';

import { TaskType } from '@scale/shared-types';
import { useOnboarding } from 'react-scale/dashboard.scale.com/contexts/OnboardingContext';
import ErrorBoundary from 'dashboard/components/SelfServe/TaskSetup/RapidTaskErrorBoundary';
import useUnprocessedAttachments from 'react-scale/studio/hooks/useUnprocessedAttachments';
import _ from 'lodash';
import { UploadedAsset } from 'react-scale/dashboard.scale.com/components/SelfServe/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConfirmationModal from 'react-scale/dashboard.scale.com/components/SelfServe/Util/ConfirmationModal';

export const CAPTION_PLACEHOLDER =
  'What does this example illustrate? Click here to add a description.';
interface ExampleLabelModalProps {
  modalOpen: boolean;
  project: Project;
  uploadedAssets: UploadedAsset[];
  taxonomy: $FIXME;
  setModalOpen: (arg: boolean) => void;
  setTaskId?: any;
  onBackToAssetSelection: () => void;
  setTasksWithProcessingAnnotationsResponses?: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  >;
  initialCaption?: string;
  resetNormalAssetCaption?: () => void;
}

const ExampleLabelModal: React.FC<ExampleLabelModalProps> = ({
  modalOpen,
  project,
  uploadedAssets,
  taxonomy,
  setModalOpen,
  setTaskId,
  onBackToAssetSelection,
  setTasksWithProcessingAnnotationsResponses,
  initialCaption,
  resetNormalAssetCaption,
}) => {
  const classes = useStyles();
  const [currAssetIndex, setCurrAssetIndex] = useState(0);
  const [attachmentMap] = useState<Record<string, string>>({}); // storageUrl -> signedUrl
  const [multiPageAttachmentMap] = useState<Record<string, string[]>>({}); // storageUrl -> signedUrl[], for multi-page documents
  const [disabled, setDisabled] = useState(false);
  const [createLog, setCreateLog] = useState(false);
  const [captionVal, setCaption] = useState(initialCaption ?? '');

  const [taskProps, setTaskProps] = useState<any>(undefined);
  const [errorMsg, setErrorMsg] = useState('');
  const [created, setCreated] = useState(false);
  const [nextAssetIndex, setNextAssetIndex] = useState(-1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { fetchUnprocessedAttachments } = useUnprocessedAttachments();

  const { taskType } = useOnboarding();

  const fetchAsset = async (assetId: string) => {
    if (!attachmentMap[assetId]) {
      attachmentMap[assetId] = (await API.selfserve.fetchAssetS3URL({ assetId })).signedURL;
    }
    return attachmentMap[assetId];
  };

  const fetchAssetPages = async (assetId: string) => {
    if (!multiPageAttachmentMap[assetId]) {
      multiPageAttachmentMap[assetId] = (await API.selfserve.fetchAssetPages({ assetId })).pageURLs;
    }
    return multiPageAttachmentMap[assetId];
  };

  const fetchAssetToTemplateAttachment = async (assetId: string, project: Project) => {
    const { attachment } = await API.selfserve.fetchAssetToTemplateAttachment({
      assetId,
      project: project.id,
    });
    return attachment;
  };

  useEffect(() => {
    if (modalOpen) setCurrAssetIndex(0);
  }, [modalOpen]);

  useEffect(() => {
    if (initialCaption) setCaption(initialCaption);
  }, [initialCaption]);

  useEffect(() => {
    let canceled = false;
    async function fetchData() {
      if (uploadedAssets && uploadedAssets.length > 0) {
        setTaskProps(undefined);

        const params = await createParams(
          taskType ?? project.type,
          uploadedAssets[currAssetIndex],
          taxonomy,
          true,
          fetchAsset,
          fetchAssetPages,
          project.useTextCollectionTemplate ? fetchAssetToTemplateAttachment : undefined,
          project,
        );

        if (params.attachments && _.every(params.attachments, a => a.type === 'unprocessed')) {
          params.attachments = await fetchUnprocessedAttachments(params.attachments ?? []);
        }

        if (!canceled) {
          setTaskProps({
            ...sampleTask,
            type: taskType ?? project.type,
            params,
            assignment: {
              projectId: project.id,
              params,
              type: taskType ?? project.type,
              featureFlags: {
                canLeaveInlineComments: true,
                canSeeInlineComments: true,
                showSidebar:
                  (taskType ?? project.type) === TaskType.NamedEntityRecognition ? true : undefined,
              },
            },
            timerDisabled: true,
            shouldWarnOnExit: false,
            readOnly: false,
            editable: true,
            canEdit: true, // used in video annotation
            isCustomer: true,
            showHeader: false,
            isTaxonomyDisplay: true,
            showFooter: false,
          });
        }
      }

      if (
        uploadedAssets &&
        uploadedAssets[currAssetIndex + 1] &&
        (taskType ?? project.type) === TaskType.DocumentTranscription
      ) {
        // warm cache for the next asset
        API.selfserve.fetchAssetPages({ assetId: uploadedAssets[currAssetIndex + 1]._id });
      }
    }

    fetchData();
    return function cleanup() {
      canceled = true;
    };
  }, [project, uploadedAssets, taxonomy, currAssetIndex]);

  const taskRef = useRef<$FIXME>();
  const onContinue = async () => {
    setDisabled(true);
    if (taskRef.current && project) {
      // get current response
      const response = await taskRef.current.getReviewChanges();
      const uploadedAsset = uploadedAssets[currAssetIndex]._id;

      const body = {
        project: project.id,
        type: taskType ?? project.type,
        uploadedAsset,
        taxonomy,
        response,
      };
      try {
        const { taskId, msg } = await API.selfserve.setInstructionsExample(body);
        if (!msg) {
          setTaskId(taskId, undefined, captionVal);
          setTasksWithProcessingAnnotationsResponses &&
            setTasksWithProcessingAnnotationsResponses(prev => ({
              ...prev,
              [taskId]: response,
            }));
          handleCreated();
        }
        setErrorMsg('');
      } catch (e) {
        setErrorMsg('Error creating example: Verify that the response is valid');
      }
      setDisabled(false);
    }
  };

  const hasAnnotations = async () => {
    if (taskRef.current) {
      const response = await taskRef.current.getReviewChanges();
      if (taskType === TaskType.Categorization) {
        return response.taxonomies && response.taxonomies.length > 0;
      } else {
        return response.annotations && response.annotations.length > 0;
      }
    } else {
      return false;
    }
  };

  const handleCreated = () => {
    setCreated(true);
    setCreateLog(true);
    setTimeout(() => {
      setCreated(false);
      close();
    }, 1000);
  };

  const handleMove = (nextIndex: number) => {
    setNextAssetIndex(nextIndex);
    setShowConfirmation(true);
  };

  const progressMove = () => {
    setCurrAssetIndex(nextAssetIndex);
    setNextAssetIndex(-1);
    setCreateLog(false);
    setShowConfirmation(false);
  };

  const close = (event?: any) => {
    // prevent ESC from closing modal
    if (event?.type === 'keydown' && event?.keyCode === 27) return;
    setModalOpen(false);
    // reset example modal caption state in AVInstructionsExamples component
    resetNormalAssetCaption && resetNormalAssetCaption();
  };

  return (
    <>
      {showConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          message={'Do you want to change assets without saving the example?'}
          onSubmit={progressMove}
          onRequestClose={() => setShowConfirmation(false)}
        />
      )}
      <Dialog
        classes={{ root: classes.dialogRoot, paper: classes.dialog }}
        maxWidth="lg"
        open={modalOpen}
        onClose={close}
      >
        <DialogTitle>
          <div
            style={{ textAlign: 'center', fontWeight: 500, fontSize: '16px', lineHeight: '20px' }}
          >
            Create an example task
          </div>
        </DialogTitle>
        <DialogContent>
          {(!uploadedAssets || uploadedAssets.length === 0) && <h6>No assets to display...</h6>}
          {uploadedAssets && uploadedAssets.length > 0 && (
            <>
              {errorMsg.length > 0 && (
                <Alert severity="error" style={{ marginTop: '2px', marginBottom: '12px' }}>
                  {errorMsg}
                </Alert>
              )}
              {created && (
                <Alert severity="success" style={{ marginTop: '2px', marginBottom: '12px' }}>
                  Example created!
                </Alert>
              )}
              {taskProps ? (
                <ErrorBoundary>
                  <Suspense fallback={<Spinner style={{ paddingBottom: '16px' }} />}>
                    <div className="component">
                      <ShortcutsProvider>
                        <TaskComponent
                          taskType={taskProps.type}
                          taskProps={taskProps}
                          taskRef={taskRef}
                          isTaxonomyDisplay={(taskType ?? project.type) !== 'categorization'}
                        />
                      </ShortcutsProvider>
                    </div>
                  </Suspense>
                </ErrorBoundary>
              ) : (
                // show spinner while task data is still being fetched
                <Spinner style={{ paddingBottom: '16px' }} />
              )}

              <div className={classes.captionContainer}>
                <p className={classes.captionHeader}>Caption</p>
                <input
                  value={captionVal}
                  placeholder={CAPTION_PLACEHOLDER}
                  className={classes.editableCaption}
                  onFocus={e => (e.target.placeholder = '')}
                  onBlur={e => (e.target.placeholder = CAPTION_PLACEHOLDER)}
                  onChange={e => {
                    e.preventDefault();
                    setCaption(e.target.value);
                  }}
                />
              </div>

              <div className={classes.selectorControls}>
                <Tooltip arrow placement="top" title="jump to first asset">
                  <div>
                    <a
                      onClick={async () => {
                        if (!disabled) {
                          if (!createLog && (await hasAnnotations())) {
                            handleMove(0);
                          } else {
                            setCurrAssetIndex(0);
                            setCreateLog(false);
                          }
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon="angle-double-left"
                        color="black"
                        style={{ width: 16, height: 16, marginLeft: 8, marginTop: 4 }}
                      />
                    </a>
                  </div>
                </Tooltip>
                <Tooltip arrow placement="top" title="view previous asset">
                  <div>
                    <a
                      style={{ marginLeft: '8px', marginRight: '8px' }}
                      onClick={async () => {
                        const index =
                          (currAssetIndex - 1 + uploadedAssets.length) % uploadedAssets.length;
                        if (!disabled) {
                          if (!createLog && (await hasAnnotations())) {
                            handleMove(index);
                          } else {
                            setCurrAssetIndex(index);
                            setCreateLog(false);
                          }
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon="angle-left"
                        color="black"
                        style={{ width: 16, height: 16, marginLeft: 8, marginTop: 4 }}
                      />
                    </a>
                  </div>
                </Tooltip>
                <p style={{ margin: '0px 10px 0px 10px' }}>
                  {currAssetIndex + 1} of {uploadedAssets.length}
                </p>
                <Tooltip arrow placement="top" title="view next asset">
                  <div>
                    <a
                      style={{ marginRight: '8px' }}
                      onClick={async () => {
                        const index = (currAssetIndex + 1) % uploadedAssets.length;
                        if (!disabled) {
                          if (!createLog && (await hasAnnotations())) {
                            handleMove(index);
                          } else {
                            setCurrAssetIndex(index);
                            setCreateLog(false);
                          }
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon="angle-right"
                        color="black"
                        style={{ width: 16, height: 16, marginLeft: 8, marginTop: 4 }}
                      />
                    </a>
                  </div>
                </Tooltip>
                <Tooltip arrow placement="top" title="jump to last asset">
                  <div>
                    <a
                      onClick={async () => {
                        const index = uploadedAssets.length - 1;
                        if (!disabled) {
                          if (!createLog && (await hasAnnotations())) {
                            handleMove(index);
                          } else {
                            setCurrAssetIndex(index);
                            setCreateLog(false);
                          }
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon="angle-double-right"
                        color="black"
                        style={{ width: 16, height: 16, marginLeft: 8, marginTop: 4 }}
                      />
                    </a>
                  </div>
                </Tooltip>
              </div>
            </>
          )}
          <style jsx global>{`
            .component {
              height: 540px;
            }
            .component,
            .component .task {
              flex-grow: 1;
              display: flex;
              flex-direction: column;
              min-height: 0;
              min-width: 0;
              width: 100%;
              overflow: auto;
            }
            .task__splitpane {
              position: relative;
              overflow: auto;
            }
          `}</style>
        </DialogContent>
        <DialogActions>
          <Button type="tertiary" onClick={onBackToAssetSelection} disabled={disabled}>
            Back to Asset Selection
          </Button>
          <Button type="primary" onClick={onContinue} disabled={disabled}>
            {disabled ? 'Saving' : 'Save example'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexFlow: 'column nowrap',
    flexWrap: 'nowrap',
  },
  dialog: {
    width: '100%',
    overflow: 'hidden',
    margin: '2% auto 20px',
  },
  dialogRoot: {
    zIndex: '110 !important' as any,
  },
  exampleTypeSelector: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  exampleType: {
    margin: '8px',
    padding: '2px 8px 2px 8px',
    border: '1px solid #E6E6E6',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  // image selector controls
  selectorControls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '24px',
  },
  createButton: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  editableCaption: {
    opacity: 0.9,
    marginTop: '0px',
    textAlign: 'center',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    width: '100%',
  },
  captionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  captionHeader: {
    width: '100%',
    lineHeight: 1,
    color: 'darkslategray',
    marginBottom: '8px',
  },
});

export default ExampleLabelModal;
